// Battery runway (GET devices/<mac>/power/, GET power/): the server's answer
// read into what the banner and the Battery & solar card show. Every value is
// checked here, so a server without the endpoint (or a mocked one) gives
// null and nothing is drawn.

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// "18 h", "4.5 days", "12 days"
export const daysText = (days) => {
  const d = num(days);
  if (d === null) return null;
  if (d < 1) return `${Math.max(1, Math.round(d * 24))} h`;
  return d >= 10 ? `${Math.round(d)} days` : `${d.toFixed(1)} days`;
};

const SEVERITY_RANK = { critical: 0, warning: 1 };

export function readAlert(a) {
  if (!a || typeof a !== "object" || typeof a.reason !== "string") return null;
  return {
    rule: String(a.rule || ""),
    title: String(a.title || "Battery"),
    severity: a.severity === "critical" ? "critical" : "warning",
    reason: a.reason,
    help: typeof a.help === "string" ? a.help : "",
  };
}

// The node's answer, or null when it is not one (no endpoint, an error page,
// a mocked loader returning something else).
export function readPower(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.alerts)) return null;
  const f = data.forecast && typeof data.forecast === "object" ? data.forecast : {};
  const b = data.battery && typeof data.battery === "object" ? data.battery : {};
  const bal = data.balance && typeof data.balance === "object" ? data.balance : null;
  const alerts = data.alerts
    .map(readAlert)
    .filter(Boolean)
    .sort((x, y) => SEVERITY_RANK[x.severity] - SEVERITY_RANK[y.severity]);
  const days = (Array.isArray(data.days) ? data.days : [])
    .filter((d) => d && typeof d.date === "string")
    .map((d) => ({
      date: d.date,
      label: d.date.slice(8, 10),
      harvest: num(d.harvest_mah) ?? 0,
      use: num(d.use_mah) ?? 0,
      complete: !!d.complete,
    }));
  const soc = Array.isArray(b.soc_pct) && b.soc_pct.length === 3 ? b.soc_pct.map(num) : null;
  return {
    externalPower: !!data.external_power,
    status: typeof data.status === "string" ? data.status : "ok",
    battery: {
      v: num(b.v),
      band: num(b.band),
      soc: soc && soc.every((x) => x !== null) ? soc : null,
    },
    balance: bal
      ? {
          use: num(bal.use_mah_day),
          harvest: num(bal.harvest_mah_day),
          net: num(bal.net_mah_day),
          days: num(bal.days),
        }
      : null,
    forecast: {
      daysLeft: f.draining ? num(f.days_left) : null,
      range: Array.isArray(f.range) ? f.range.map(num) : null,
      confidence: typeof f.confidence === "string" ? f.confidence : null,
      basis: typeof f.basis === "string" ? f.basis : null,
      draining: !!f.draining,
      text: typeof f.text === "string" ? f.text : "",
    },
    days,
    alerts,
    lastSeen: typeof data.last_seen === "string" ? data.last_seen : null,
  };
}

// The headline: "About 4.5 days left (low confidence)", "Harvest covers use",
// "On external power", or null without a forecast yet.
export function runwayText(power) {
  if (!power) return null;
  if (power.externalPower) return "On external power";
  const f = power.forecast;
  if (f.daysLeft !== null)
    return `About ${daysText(f.daysLeft)} left${f.confidence ? ` (${f.confidence} confidence)` : ""}`;
  if (f.text.startsWith("Harvest covers use")) return "Harvest covers use";
  if (f.text.startsWith("The battery is near full")) return "Near full, panel working";
  return null;
}

// The banner shows only real battery warnings: none on external power.
export const bannerAlerts = (power) =>
  power && !power.externalPower ? power.alerts : [];

export const worstSeverity = (alerts) =>
  alerts.some((a) => a.severity === "critical") ? "critical" : alerts.length ? "warning" : null;

// The fleet list (GET power/): nodes with at least one alert, most urgent first
// as the server sorts them.
export function readFleet(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.devices)) return [];
  return data.devices
    .filter((d) => d && typeof d.mac === "string" && Array.isArray(d.alerts))
    .map((d) => ({
      mac: d.mac,
      name: typeof d.name === "string" && d.name ? d.name : d.mac,
      externalPower: !!d.external_power,
      daysLeft: num(d.days_left),
      alerts: d.alerts.map(readAlert).filter(Boolean),
    }))
    .filter((d) => d.alerts.length > 0 && !d.externalPower);
}
